import { useCallback, useEffect, useState } from 'react';
import { formatMinor, type CurrencyCode, type SubscriptionDto } from '@trestly/shared';
import { useCreateSubscription, useDeleteSubscription, useUpdateSubscription } from '../api/hooks';
import { AmountInput, tryParseAmount } from './AmountInput';
import { CurrencyToggle } from './CurrencyToggle';
import { Field } from './Field';
import { Sheet } from './Sheet';
import { Spinner } from './Spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { errorText } from '../lib/errors';
import { confirmAction, haptic } from '@/telegram';
import { t } from '../i18n';

interface SubscriptionSheetProps {
  open: boolean;
  subscription: SubscriptionDto | null;
  onClose: () => void;
}

export function SubscriptionSheet({ open, subscription, onClose }: SubscriptionSheetProps): JSX.Element {
  const createSubscription = useCreateSubscription();
  const updateSubscription = useUpdateSubscription();
  const deleteSubscription = useDeleteSubscription();

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('BYN');
  const [billingDay, setBillingDay] = useState('1');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    if (subscription) {
      setName(subscription.name);
      setAmount(formatMinor(subscription.amountMinor, subscription.currency));
      setCurrency(subscription.currency);
      setBillingDay(String(subscription.billingDay));
      setNote(subscription.note ?? '');
    } else {
      setName('');
      setAmount('');
      setCurrency('BYN');
      setBillingDay('1');
      setNote('');
    }
    createSubscription.reset();
    updateSubscription.reset();
    deleteSubscription.reset();
    // мутации меняются каждый рендер — намеренно не в зависимостях
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscription]);

  const day = Number(billingDay);
  const dayValid = Number.isInteger(day) && day >= 1 && day <= 31;
  const nameValid = name.trim().length > 0;
  const busy =
    createSubscription.isPending || updateSubscription.isPending || deleteSubscription.isPending;

  const error =
    createSubscription.error ??
    updateSubscription.error ??
    deleteSubscription.error ??
    null;

  const handleSubmit = useCallback((): void => {
    setSubmitted(true);
    const amountMinor = tryParseAmount(amount, currency);
    if (amountMinor === undefined || !dayValid || !nameValid) {
      haptic.result('error');
      return;
    }

    const payload = {
      name: name.trim(),
      amountMinor,
      currency,
      billingDay: day,
      note: note.trim() === '' ? null : note.trim(),
    };
    const done = {
      onSuccess: () => {
        haptic.result('success');
        onClose();
      },
      onError: () => haptic.result('error'),
    };

    if (subscription) updateSubscription.mutate({ id: subscription.id, patch: payload }, done);
    else createSubscription.mutate(payload, done);
    // мутации нестабильны между рендерами — сознательно вне зависимостей
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currency, day, dayValid, nameValid, name, note, subscription, onClose]);

  const handleDelete = async (): Promise<void> => {
    if (!subscription) return;
    const ok = await confirmAction(t('components.sub.deleteConfirm', { name: subscription.name }));
    if (!ok) return;
    // Удаление мягкое (archive) на бэке — история не разъезжается.
    deleteSubscription.mutate(subscription.id, {
      onSuccess: () => {
        haptic.result('success');
        onClose();
      },
      onError: () => haptic.result('error'),
    });
  };

  return (
    <Sheet
      open={open}
      title={subscription ? subscription.name : t('components.sub.new')}
      description={subscription ? t('components.sub.subtitle') : undefined}
      onClose={onClose}
    >
      <Field label={t('components.name')} error={submitted && !nameValid ? t('components.nameRequired') : null}>
        <Input
          type="text"
          value={name}
          aria-invalid={submitted && !nameValid}
          onChange={(event) => setName(event.target.value)}
          placeholder="Spotify"
        />
      </Field>

      <AmountInput
        value={amount}
        onChange={setAmount}
        currency={currency}
        showError={submitted}
        label={t('components.sub.amount', { currency })}
        trailing={
          <CurrencyToggle
            value={currency}
            onChange={setCurrency}
            size="compact"
            className="h-15"
            disabled={busy}
          />
        }
      />

      <Field
        label={t('components.sub.day')}
        error={submitted && !dayValid ? t('components.dayError') : null}
        hint={t('components.sub.dayHint')}
      >
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          max={31}
          value={billingDay}
          aria-invalid={submitted && !dayValid}
          onChange={(event) => setBillingDay(event.target.value)}
        />
      </Field>

      <Field label={t('components.note')} hint={t('components.sub.noteHint')}>
        <Input
          type="text"
          value={note}
          placeholder={t('components.optional')}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>

      {error ? <div className="mb-3 text-[13px] text-destructive">{errorText(error)}</div> : null}

      <Button className="w-full" disabled={busy} onClick={handleSubmit}>
        {busy && updateSubscription.isPending ? <Spinner small /> : t('components.save')}
      </Button>

      {subscription ? (
        <Button
          variant="destructive"
          className="mt-2 w-full"
          disabled={busy}
          onClick={() => void handleDelete()}
        >
          {t('components.sub.delete')}
        </Button>
      ) : null}
    </Sheet>
  );
}
